import { useSelector } from '@xstate/react';
import { ModalDialog } from '~/features/vault/documents/ModalDialog';
import type { ListingSide } from '../b2bSideCopy';
import { useNegotiationRef } from '../marketSelectors';
import { NegotiationDetails } from './NegotiationDetails';
import type { DraftSubmitResult, NegotiationParty, NegotiationResponseType } from './types';

type ResponseNegotiationModalProps = {
  open: boolean;
  responseId: number | null;
  responseType: NegotiationResponseType;
  viewerRole?: NegotiationParty;
  onClose: () => void;
  // [MCV-1646] draft-first initial offer -- render on a bare listing that has
  // no JobResponse yet.
  draftSource?: { listingType: 'JOB' | 'MSAAS'; listingId: number };
  draftProviderIds?: number[];
  startDraftIfEmpty?: boolean;
  onDraftSubmitted?: (result: DraftSubmitResult) => void;
  // [MCV-1889] The response's listing side (party labels) and whether its listing filled.
  listingSide?: ListingSide | null;
  listingFilled?: boolean;
};

export const ResponseNegotiationModal = ({
  open,
  responseId,
  responseType,
  viewerRole,
  onClose,
  draftSource,
  draftProviderIds,
  startDraftIfEmpty,
  onDraftSubmitted,
  listingSide,
  listingFilled,
}: ResponseNegotiationModalProps) => {
  const { negotiationRef } = useNegotiationRef();
  const submitLifecycleBusy = useSelector(
    negotiationRef,
    (state) => state?.context.submitLifecycleBusy ?? false
  );

  if (responseId == null && draftSource == null) return null;

  const handleClose = () => {
    if (!negotiationRef?.getSnapshot().context.submitLifecycleBusy) onClose();
  };

  return (
    <ModalDialog
      open={open}
      onClose={handleClose}
      closeDisabled={submitLifecycleBusy}
      title="Negotiation"
      xButton
    >
      <NegotiationDetails
        responseId={responseId ?? undefined}
        responseType={responseType}
        viewerRole={viewerRole}
        // [MCV-1816] Not the gated handleClose: Details auto-closes after a
        // successful offer/counter while the post-submit reload is still in
        // flight (submitLifecycleBusy), and its own Close button already
        // refuses to act while busy.
        onClose={onClose}
        draftSource={draftSource}
        draftProviderIds={draftProviderIds}
        startDraftIfEmpty={startDraftIfEmpty}
        onDraftSubmitted={onDraftSubmitted}
        listingSide={listingSide}
        listingFilled={listingFilled}
      />
    </ModalDialog>
  );
};
